import { useEffect, useMemo, useState } from 'react';
import {
  developerIdSchema,
  type AppSettings,
  type DeveloperProfile,
  type DiscoveredEntities,
  type Tool,
} from '@ace-tracker/shared';
import { Button } from '../Button';
import { TOOL_LABELS } from '../Sidebar';
import { Card, SaveBar, SelectInput, TextInput } from './formParts';
import { useSaveSettings } from './useSaveSettings';

interface DraftRow {
  displayName: string;
  team: string;
  excluded: boolean;
}

type Draft = Record<string, DraftRow>;
type Filter = 'all' | 'unnamed' | 'excluded';

const EMPTY_ROW: DraftRow = { displayName: '', team: '', excluded: false };

function toDraft(profiles: DeveloperProfile[]): Draft {
  return Object.fromEntries(
    profiles.map((p) => [p.developerId, { displayName: p.displayName ?? '', team: p.team ?? '', excluded: p.excluded }]),
  );
}

/** Only rows carrying a value are persisted — a developer with no profile simply shows their id. */
function toProfiles(draft: Draft): DeveloperProfile[] {
  return Object.entries(draft)
    .map(([developerId, row]) => ({
      developerId,
      displayName: row.displayName.trim() || undefined,
      team: row.team.trim() || undefined,
      excluded: row.excluded,
    }))
    .filter((p) => p.displayName || p.team || p.excluded)
    .sort((a, b) => a.developerId.localeCompare(b.developerId));
}

function sameProfiles(a: DeveloperProfile[], b: DeveloperProfile[]): boolean {
  const norm = (list: DeveloperProfile[]) =>
    JSON.stringify(
      [...list]
        .sort((x, y) => x.developerId.localeCompare(y.developerId))
        .map((p) => [p.developerId, p.displayName ?? '', p.team ?? '', p.excluded]),
    );
  return norm(a) === norm(b);
}

export function DevelopersSettings({
  settings,
  discovered,
  discoveryError,
}: {
  settings: AppSettings;
  discovered?: DiscoveredEntities;
  discoveryError?: string;
}) {
  const baseline = settings.developers;
  const [draft, setDraft] = useState<Draft>(() => toDraft(baseline));
  const [manualIds, setManualIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [newId, setNewId] = useState('');
  const [newIdError, setNewIdError] = useState<string>();
  const { save, saving, error, savedAt, reset } = useSaveSettings('developers');

  const baselineKey = JSON.stringify(baseline);
  useEffect(() => setDraft(toDraft(baseline)), [baselineKey]);

  const seen = useMemo(
    () => new Map((discovered?.developers ?? []).map((d) => [d.developerId, d])),
    [discovered],
  );

  const rows = useMemo(() => {
    const ids = new Set([...seen.keys(), ...Object.keys(draft), ...manualIds]);
    const q = search.trim().toLowerCase();
    return [...ids]
      .map((id) => ({ id, row: draft[id] ?? EMPTY_ROW, seen: seen.get(id) }))
      .filter(({ id, row }) => {
        if (filter === 'unnamed' && row.displayName.trim()) return false;
        if (filter === 'excluded' && !row.excluded) return false;
        if (!q) return true;
        return [id, row.displayName, row.team].some((v) => v.toLowerCase().includes(q));
      })
      .sort((a, b) => a.id.localeCompare(b.id));
  }, [seen, draft, manualIds, search, filter]);

  const profiles = toProfiles(draft);
  const dirty = !sameProfiles(profiles, baseline);
  const namedCount = profiles.filter((p) => p.displayName).length;
  const excludedCount = profiles.filter((p) => p.excluded).length;

  function update(id: string, patch: Partial<DraftRow>) {
    setDraft((d) => ({ ...d, [id]: { ...(d[id] ?? EMPTY_ROW), ...patch } }));
  }

  function addManual() {
    const parsed = developerIdSchema.safeParse(newId.trim());
    if (!parsed.success) {
      setNewIdError('Enter a username (letters, digits, . - _ @ \\) or a 16-character hash.');
      return;
    }
    const known = [...seen.keys(), ...Object.keys(draft), ...manualIds];
    if (!known.some((id) => id.toLowerCase() === parsed.data.toLowerCase())) {
      setManualIds((ids) => [...ids, parsed.data]);
    }
    setSearch(parsed.data);
    setFilter('all');
    setNewId('');
    setNewIdError(undefined);
  }

  return (
    <div className="space-y-5">
      <Card
        title="Developer names"
        description={
          <>
            Map each collector username (or hash) to a person's name. Names replace the username everywhere in the dashboard;
            developers without a name keep showing their username. <b className="font-medium">Exclude</b> removes test or
            service accounts from every figure — the telemetry itself is untouched and they can be re-included at any time.
          </>
        }
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[14rem] flex-1">
            <label htmlFor="dev-search" className="sr-only">
              Search developers
            </label>
            <TextInput
              id="dev-search"
              type="search"
              placeholder="Search username, name, or team"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="w-56">
            <label htmlFor="dev-filter" className="sr-only">
              Show
            </label>
            <SelectInput id="dev-filter" value={filter} onChange={(e) => setFilter(e.target.value as Filter)}>
              <option value="all">All developers</option>
              <option value="unnamed">Without a display name</option>
              <option value="excluded">Excluded only</option>
            </SelectInput>
          </div>
        </div>

        <p className="mt-3 text-xs text-text-muted">
          {namedCount} named · {excludedCount} excluded ·{' '}
          {discovered
            ? `${discovered.developers.length} seen in the last ${discovered.days} days`
            : discoveryError
              ? 'Could not load developers from telemetry — configured developers are still listed.'
              : 'Loading developers from telemetry…'}
        </p>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[820px] text-left text-sm">
            <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
              <tr>
                <th className="pb-2.5 pr-3">Username</th>
                <th className="w-[28%] pb-2.5 pr-3">Display name</th>
                <th className="w-[20%] pb-2.5 pr-3">Team</th>
                <th className="w-[7rem] pb-2.5 text-center">Exclude</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ id, row, seen: s }) => (
                <tr key={id} className={`border-b border-border last:border-0 ${row.excluded ? 'bg-surface-alt' : ''}`}>
                  <td className="py-2 pr-3 align-middle">
                    <span className="block break-all font-mono text-xs text-text-default">{id}</span>
                    <span className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-text-faint">
                      {s ? (
                        <>
                          {s.tools.map((t: Tool) => (
                            <span key={t} className="rounded-input bg-surface-alt px-1.5 py-0.5 text-text-muted">
                              {TOOL_LABELS[t]}
                            </span>
                          ))}
                          <span>last active {new Date(s.lastActive).toLocaleDateString()}</span>
                        </>
                      ) : (
                        <span>Not seen in recent telemetry</span>
                      )}
                    </span>
                  </td>
                  <td className="py-2 pr-3">
                    <TextInput
                      aria-label={`Display name for ${id}`}
                      placeholder={id}
                      maxLength={100}
                      value={row.displayName}
                      onChange={(e) => update(id, { displayName: e.target.value })}
                    />
                  </td>
                  <td className="py-2 pr-3">
                    <SelectInput
                      aria-label={`Team for ${id}`}
                      value={row.team}
                      onChange={(e) => update(id, { team: e.target.value })}
                    >
                      <option value="">No team</option>
                      {settings.teams.map((team) => <option key={team} value={team}>{team}</option>)}
                    </SelectInput>
                  </td>
                  <td className="py-2 text-center">
                    <label className={`inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-input focus-within:outline focus-within:outline-2 focus-within:outline-sky-blue`}>
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-[var(--primary-blue)]"
                        aria-label={`Exclude ${id} from the dashboard`}
                        checked={row.excluded}
                        onChange={(e) => update(id, { excluded: e.target.checked })}
                      />
                    </label>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={4} className="py-4 text-text-muted">
                    No developers match. Add one below if they haven't reported usage yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-5 border-t border-border pt-4">
          <label htmlFor="dev-add" className="block text-sm font-medium text-text-default">
            Add a developer not seen in telemetry yet
          </label>
          <div className="mt-1.5 flex flex-wrap items-start gap-3">
            <div className="min-w-[14rem] flex-1 sm:max-w-sm">
              <TextInput
                id="dev-add"
                placeholder="e.g. jdoe or CORP\jdoe"
                value={newId}
                invalid={Boolean(newIdError)}
                aria-describedby={newIdError ? 'dev-add-error' : undefined}
                onChange={(e) => setNewId(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addManual();
                  }
                }}
              />
              {newIdError && (
                <p id="dev-add-error" className="mt-1 text-xs text-error">
                  {newIdError}
                </p>
              )}
            </div>
            <Button type="button" variant="secondary" onClick={addManual} disabled={!newId.trim()}>
              <i aria-hidden="true" className="fa-solid fa-plus" />
              Add developer
            </Button>
          </div>
        </div>
      </Card>

      <SaveBar
        dirty={dirty}
        saving={saving}
        error={error}
        savedAt={savedAt}
        audit={settings.audit.developers}
        onSave={() => save(profiles)}
        onDiscard={() => {
          setDraft(toDraft(baseline));
          setManualIds([]);
          reset();
        }}
      />
    </div>
  );
}
